import { useCallback, useMemo } from 'react';

import { configToDropdownOption } from '../../../../../../common/Dropdown';
import type { DropdownOption, SingleSelectValue } from '../../../../../../common/Dropdown/Dropdown';
import { isRecognizedOption } from '../../../../../../common/Dropdown/utils';
import type { AdTagType, EventType, UNRECOGNIZED } from '../../../../../../configs';
import { adTagTypes, getPermittedAdTagEvents, isRecognizedConfig } from '../../../../../../configs';
import { convertAdTagEvent } from '../../../../../../configs/adTagEvents';
import type { TagSheetTag } from '../../types';
import { getDefaultPositionForType } from './utils';

const allTagTypeOptions: DropdownOption<AdTagType>[] = Object.values(adTagTypes).map(configToDropdownOption);

type UseTagRowProps = {
  tag: TagSheetTag;
  siblingTags: TagSheetTag[];
  onEditTag: (updatedTag: TagSheetTag) => void;
  lineItemBillableEvent?: string | null;
};

type UseTagRowReturn = {
  showPosition: boolean;
  tagTypeOptions: DropdownOption<AdTagType>[];
  selectedTypeOption: DropdownOption<AdTagType> | null;
  positionOptions: DropdownOption<EventType>[];
  selectedPositionOption: DropdownOption<EventType> | null;
  isPositionDisabled: boolean;
  handleTypeChange: (option: SingleSelectValue<AdTagType | typeof UNRECOGNIZED>) => void;
  handlePositionChange: (option: SingleSelectValue<EventType | typeof UNRECOGNIZED>) => void;
  handleTagUrlSave: (tagUrl: string) => void;
};

const useTagRow = ({ tag, siblingTags, onEditTag, lineItemBillableEvent }: UseTagRowProps): UseTagRowReturn => {
  const showPosition = tag.type.key !== adTagTypes.CLICK.key;

  const tagTypeOptions: DropdownOption<AdTagType>[] = useMemo(() => {
    const isBillableTakenBySibling = siblingTags.some(
      (siblingTag) => siblingTag !== tag && siblingTag.type.key === adTagTypes.BILLABLE.key
    );
    const isClickTakenBySibling = siblingTags.some(
      (siblingTag) => siblingTag !== tag && siblingTag.type.key === adTagTypes.CLICK.key
    );

    return allTagTypeOptions.map((option) => {
      const shouldDisable =
        (isBillableTakenBySibling && option.value === adTagTypes.BILLABLE.key) ||
        (isClickTakenBySibling && option.value === adTagTypes.CLICK.key);

      return shouldDisable ? { ...option, isDisabled: true } : option;
    });
  }, [tag, siblingTags]);

  const selectedTypeOption = useMemo(() => tagTypeOptions.find((opt) => opt.value === tag.type.key) || null, [
    tag.type,
    tagTypeOptions,
  ]);

  const positionOptions: DropdownOption<EventType>[] = useMemo(
    () =>
      showPosition && isRecognizedConfig(tag.type)
        ? getPermittedAdTagEvents(tag.type.key, lineItemBillableEvent ?? undefined).map(configToDropdownOption)
        : [],
    [tag.type, showPosition, lineItemBillableEvent]
  );

  const selectedPositionOption = useMemo(() => positionOptions.find((opt) => opt.value === tag.position.key) || null, [
    positionOptions,
    tag.position,
  ]);

  const isPositionDisabled = positionOptions.length <= 1;

  const handleTypeChange = useCallback(
    (option: SingleSelectValue<AdTagType | typeof UNRECOGNIZED>): void => {
      if (isRecognizedOption(option)) {
        const currentPosition = isRecognizedConfig(tag.position) ? tag.position.key : undefined;
        const newPosition = getDefaultPositionForType(option.value, currentPosition, lineItemBillableEvent);
        onEditTag({
          ...tag,
          type: adTagTypes[option.value],
          position: convertAdTagEvent(newPosition),
        });
      }
    },
    [tag, onEditTag, lineItemBillableEvent]
  );

  const handlePositionChange = useCallback(
    (option: SingleSelectValue<EventType | typeof UNRECOGNIZED>): void => {
      if (isRecognizedOption(option)) {
        onEditTag({ ...tag, position: convertAdTagEvent(option.value) });
      }
    },
    [tag, onEditTag]
  );

  const handleTagUrlSave = useCallback(
    (tagUrl: string): void => {
      onEditTag({ ...tag, tagUrl });
    },
    [tag, onEditTag]
  );

  return {
    showPosition,
    tagTypeOptions,
    selectedTypeOption,
    positionOptions,
    selectedPositionOption,
    isPositionDisabled,
    handleTypeChange,
    handlePositionChange,
    handleTagUrlSave,
  };
};

export default useTagRow;
